<script>
import {defineComponent, onMounted, ref} from 'vue'
import {useDeliveryStore} from "@js/store/DeliveryStore.js";

export default defineComponent({
    name: 'DeliveryZonesMap',
    props: {
        // центр склад/база
        center: {type: Array, default: () => [55.751244, 37.618423]}, // Москва
        // описание зон (в км). Порядок: от меньшего к большему
        zones: {
            type: Array,
            default: () => ([
                {id: 1, name: 'Зона 1', from_km: 0, to_km: 10, coeff: 1.00, color: '#ffcc00', opacity: 0.35},
                {id: 2, name: 'Зона 2', from_km: 10, to_km: 25, coeff: 1.15, color: '#ffa07a', opacity: 0.30},
                {id: 3, name: 'Зона 3', from_km: 25, to_km: 45, coeff: 1.30, color: '#7fffd4', opacity: 0.28},
                {id: 4, name: 'Зона 4', from_km: 45, to_km: 70, coeff: 1.50, color: '#87cefa', opacity: 0.26},
                {id: 5, name: 'Зона 5', from_km: 70, to_km: 100, coeff: 1.75, color: '#20b2aa', opacity: 0.24},
                // всё, что дальше — «за зоной 5»: обработаем логикой findZone()
            ])
        },
        // начальный зум
        zoom: {type: Number, default: 10},
        // показывать ли полупрозрачные зоны
        showZones: {type: Boolean, default: true}
    },
    emits: ['point-selected', 'zone-changed'],
    watch: {
        'deliveryStore.coords': function (coords){
            const ymaps = window.ymaps;
            if (!ymaps) return;
            this.setMarker(ymaps, coords);
        }
    },
    setup(props, {emit}) {
        const mapEl = ref(null);
        let map, marker;

        const loadYMaps = () => new Promise(resolve => {
            if (window.ymaps) return resolve(window.ymaps);
            const script = document.createElement('script');
            const apiKey = import.meta.env.VITE_YANDEX_GEOCODER_API_KEY || '';

            script.src =
                `https://api-maps.yandex.ru/2.1/?lang=ru_RU${apiKey ? `&apikey=${apiKey}` : ''}`;
            script.onload = () => window.ymaps.ready(() => resolve(window.ymaps));
            document.head.appendChild(script);
        });

        // геодезия: смещение точки по азимуту/дистанции (км)
        function destinationPoint([lat, lon], distanceKm, bearingDeg) {
            const R = 6371; // км
            const δ = distanceKm / R;
            const θ = bearingDeg * Math.PI / 180;
            const φ1 = lat * Math.PI / 180;
            const λ1 = lon * Math.PI / 180;

            const sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1);
            const sinδ = Math.sin(δ), cosδ = Math.cos(δ);

            const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
            const φ2 = Math.asin(sinφ2);
            const y = Math.sin(θ) * sinδ * cosφ1;
            const x = cosδ - sinφ1 * sinφ2;
            const λ2 = λ1 + Math.atan2(y, x);

            return [(φ2 * 180 / Math.PI), ((λ2 * 180 / Math.PI + 540) % 360) - 180];
        }

        // окружность как ломаная
        function circleCoords(center, radiusKm, steps = 128) {
            const res = [];
            for (let b = 0; b <= 360; b += 360 / steps) {
                res.push(destinationPoint(center, radiusKm, b));
            }
            return res;
        }

        // «пончик»: внешний контур и внутреннее отверстие
        function makeRingPolygon(ymaps, center, innerKm, outerKm, color, opacity) {
            const outer = circleCoords(center, outerKm);
            const inner = circleCoords(center, innerKm).reverse(); // отверстие — обратный обход
            const polygon = new ymaps.Polygon([outer, inner], {}, {
                fillColor: color,
                fillOpacity: opacity,
                strokeColor: color,
                strokeOpacity: 0.5,
                strokeWidth: 1
            });
            return polygon;
        }

        function haversineKm([lat1, lon1], [lat2, lon2]) {
            const toRad = x => x * Math.PI / 180;
            const R = 6371;
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
            return 2 * R * Math.asin(Math.sqrt(a));
        }

        function findZone(distanceKm) {
            for (const z of props.zones) {
                if (distanceKm >= z.from_km && distanceKm < z.to_km) return z;
            }
            // «за последней зоной»
            const last = props.zones[props.zones.length - 1];
            return {id: 'beyond', name: 'За зоной', from_km: last.to_km, to_km: Infinity, coeff: null};
        }

        function setMarker(ymaps, coords) {
            if (!marker) {
                marker = new ymaps.Placemark(coords, {}, {preset: 'islands#yellowDotIcon'});
                map.geoObjects.add(marker);
            } else marker.geometry.setCoordinates(coords);

            const distKm = haversineKm(props.center, coords);
            const zone = findZone(distKm);

            emit('point-selected', {coords, distance_km: distKm});
            emit('zone-changed', zone);

            // tooltip
            const text = `${zone.name}: ~${distKm.toFixed(1)} км от склада${zone.coeff ? `, K=${zone.coeff}` : ''}`;
            marker.properties.set('balloonContent', text);
            marker.properties.set('hintContent', text);
        }

        onMounted(async () => {
            const ymaps = await loadYMaps();

            map = new ymaps.Map(mapEl.value, {
                center: props.center,
                zoom: props.zoom,
                controls: ['zoomControl', 'fullscreenControl']
            }, {suppressMapOpenBlock: true});

            // склад
            const centerMark = new ymaps.GeoObject(
                {geometry: {type: 'Point', coordinates: props.center}},
                {preset: 'islands#blackHomeIcon', draggable: false, zIndex: 500}
            );
            map.geoObjects.add(centerMark);

            // зоны
            if (props.showZones) {
                props.zones.forEach(z => {
                    const poly = makeRingPolygon(ymaps, props.center, z.from_km, z.to_km, z.color, z.opacity);
                    poly.events.add('click', (e) => setMarker(ymaps, e.get('coords')));
                    map.geoObjects.add(poly);
                });
            }

            // клик по карте — поставить точку/посчитать зону
            map.events.add('click', e => setMarker(ymaps, e.get('coords')));
        });

        const deliveryStore = useDeliveryStore()
        return {mapEl, deliveryStore, setMarker};
    }
})
</script>

<template>
    <div class="relative w-full h-[400px] md:h-[580px] rounded-3xl overflow-hidden border">
        <div ref="mapEl" class="w-full h-full"></div>
    </div>
</template>

<style scoped>
/* подчищаем атрибутику */
</style>
